import { EditorHelper } from './editor-helper'
import { EditorPropertyWithKey } from '@lg/shared'
import { BranchNameType, BusinessObjectItem } from '@lg/shared'
import { BusinessObject } from '@lg/shared'
import { BoReference } from '@lg/shared'
import { DevBarService } from '@lg/ng-shared'

export abstract class ScreenItemEditorHelper<
	BO extends BusinessObject,
	Item extends BusinessObjectItem,
> extends EditorHelper<Item> {
	protected abstract getInputAssignmentMap(item: Item): Map<string, string>
	protected abstract getOutputAssignmentMap(item: Item): Map<string, string>

	async onPropertyChanged(
		block: Item,
		property: EditorPropertyWithKey<BO, Item>,
		allProperties: EditorPropertyWithKey<BO, Item>[],
	) {
		const propertyKey = property.key as keyof Item

		if (propertyKey == 'screenRef') {
			await this.updateScreenAssignments(block, allProperties)
		}
	}

	async modifyEditorProperties(
		item: Item,
		properties: EditorPropertyWithKey<BO, Item>[],
		propertyValues: Record<string, any>,
	) {
		const { inputs, outputs } = await this.updateScreenAssignments(
			item,
			properties,
		)
		const inputAssignments = this.getInputAssignmentMap(item)
		const outputAssignments = this.getOutputAssignmentMap(item)
		let order = 101
		// console.log('inputs', inputs) // TODO: replace the approach using inputAssignments with the one using inputs

		// 		for(const input of inputs) {
		// 			const variableName = input.name
		// 			properties.push({
		// 				key: `input_${variableName}`,
		// 				order: order++,
		// 				label: variableName,
		// 				tab: 'Inputs',
		// 				inputType: 'code',
		// 				codeLanguage: 'ts',
		// 				editorSize: 'singleline',
		// 				isBinding: false,
		// 				tsReturnType: () => inputs.find(i => i.name == variableName)?.type ?? 'any',
		// 				getter(item: Item, key) {
		// 					return inputAssignments.get(variableName)
		// 				},
		// 				setter(item: Item, key, value) {
		// 					inputAssignments.set(variableName, value)
		// 				}
		// 			})
		// 		}

		for (const [variableName] of inputAssignments?.entries() ?? []) {
			properties.push({
				key: `input_${variableName}`,
				order: order++,
				label: variableName,
				tab: 'Inputs',
				inputType: 'code',
				codeLanguage: 'ts',
				editorSize: 'singleline',
				isBinding: false,
				tsReturnType: () =>
					inputs.find((i) => i.name == variableName)?.type ?? 'any',
				getter(item: Item, key) {
					return inputAssignments.get(variableName)
				},
				setter(item: Item, key, value) {
					inputAssignments.set(variableName, value)
				},
			})
		}

		for (const [outputName] of outputAssignments?.entries() ?? []) {
			properties.push({
				key: `output_${outputName}`,
				order: order++,
				label: outputName,
				tab: 'Output handlers',
				spanColumns: '1 / -1',
				inputType: 'code',
				codeLanguage: 'ts',
				editorSize: 'normal',
				isBinding: false,
				tsReturnType: () => 'void | boolean | string',
				additionalVariables: () => ({
					data:
						outputs.find((o) => o.name == outputName)?.outputTsType ?? 'any',
				}),
				getter(item: Item, key) {
					return outputAssignments.get(outputName)
				},
				setter(item: Item, key, value) {
					outputAssignments.set(outputName, value)
				},
			})
		}
	}

	protected async updateScreenAssignments(
		item: Item,
		allProperties: EditorPropertyWithKey<BO, Item>[],
	) {
		const boDetailsList =
			await this.getBoSpecificDetailsForReferencedItems(item)
		const inputs = boDetailsList.flatMap((boDetails) => boDetails?.inputs ?? [])
		const outputs = boDetailsList.flatMap(
			(boDetails) => boDetails?.outputs ?? [],
		)
		const inputAssignments = this.getInputAssignmentMap(item)
		const outputAssignments = this.getOutputAssignmentMap(item)

		// remove assignments for no-longer existing inputs
		for (const [key, value] of inputAssignments.entries()) {
			const correspondingInput = inputs.find((i) => i.name == key)
			if (!correspondingInput) {
				if (value) {
					const assignmentProperty = allProperties.find((p) => p.key == key)
					if (assignmentProperty) assignmentProperty.obsolete = true
				} else {
					inputAssignments.delete(key)
				}
			}
		}

		// add missing assignments for newly-created inputs
		for (const input of inputs) {
			if (!inputAssignments.has(input.name)) {
				inputAssignments.set(input.name, '')
			}
			const assignmentProperty = allProperties.find((p) => p.key == input.name)
			if (assignmentProperty) assignmentProperty.obsolete = false
		}

		// remove assignments for no-longer existing outputs
		for (const [key, value] of outputAssignments.entries()) {
			const correspondingOutput = outputs.find((i) => i.name == key)
			if (!correspondingOutput) {
				if (value) {
					const assignmentProperty = allProperties.find((p) => p.key == key)
					if (assignmentProperty) assignmentProperty.obsolete = true
				} else {
					outputAssignments.delete(key)
				}
			}
		}

		// add missing assignments for newly-created outputs
		for (const output of outputs) {
			if (!outputAssignments.has(output.name)) {
				outputAssignments.set(output.name, '')
			}
			const assignmentProperty = allProperties.find((p) => p.key == output.name)
			if (assignmentProperty) assignmentProperty.obsolete = false
		}

		return {
			inputs,
			outputs,
		}
	}

	protected abstract getScreenRef(item: Item): BoReference<'Screen'>

	onDoubleClick(branchName: BranchNameType, item: Item): void {
		const devBarService = this.helperService.injector.get(DevBarService)
		devBarService.openBoEditor(branchName, this.getScreenRef(item))
	}
}
